import {ChangeEvent, useEffect, useState} from "react";
import {TextField} from "@mui/material";
import style from "./CustomDomain.module.scss";
import saveIcon from "../../../assets/svg/save.svg";

import {useApi} from "../../../providers/api.provider";
import {useUser} from "../../../providers/user.provider";
import {useGuest} from "../../../providers/guest.provider";
import {ESnackBarType} from "../../ui/SnackBar/SnackBar";

type TCustomDomain = {
	externalUrl: string;// Your domain
	defaultUrl: string;// Your redirection URL
	id?: number | null;
}

const CustomDomain = () => {
	const api = useApi();
	const {user, setUser, setLoading, listSystemOptions} = useUser();
	const {setActiveSnackBar} = useGuest();
	const [stateDisabled, setStateDisabled] = useState<boolean>(true);
	const [isEdit, setIsEdit] = useState<boolean>(false);
	const [customDomainForm, setCustomDomainForm] = useState<TCustomDomain>({
		externalUrl: "",// Your domain
		defaultUrl: "",// Your redirection URL
		id: null,
	});

	const [errors, setErrors] = useState<TCustomDomain>({
		externalUrl: "",
		defaultUrl: "",
	});

	// if (!listSystemOptions) {
	// 	return <div>Loading server IP...</div>;
	// }
	const systemOptionServerIP: string | undefined = Array.isArray(listSystemOptions)
		? listSystemOptions.find(
			(option: { title: string; value: string }) =>
				option.title === "CUSTOM_DOMAIN_SERVER_IP" && option.value.length > 0
		)?.value
		: undefined;

	const ourServerIP : string = systemOptionServerIP || process.env.CONFIG_SERVER_IP || "192.168.0.1";

	const handleClickSave = async () => {
		const formToSave = {
			...customDomainForm,
			externalUrl: normalizeCustomDomainExternalUrl(customDomainForm.externalUrl),
			defaultUrl: normalizeCustomDomainUrls(customDomainForm.defaultUrl),
		};
		if (validateForm(formToSave)) {
			setLoading(true);
			await api.customDomain
				.create(formToSave)
				.then((res) => {
					// Update user context with new customDomain data
					const updatedUser = {
						...user,
						customDomain: {
							domain: formToSave.externalUrl,
							path: formToSave.defaultUrl,
							id: res.id,
							userId: user.id,
							isActive: res.isActive || false,
							created_at: res.created_at,
							updated_at: res.updated_at,
						}
					};
					setUser(updatedUser);

					// Update form with normalized values and new ID
					setCustomDomainForm({
						...formToSave,
						id: res.id,
					});
					setIsEdit(true);

					setActiveSnackBar({
						state: true,
						name: ESnackBarType.success,
						message: "Successfully saved",
					});
				})
				.catch((res) => {
					setLoading(false);
					// TODO - Need to modify error handling
					let message = "Error occurred";
					setActiveSnackBar({
						state: true,
						name: ESnackBarType.error,
						message,
					});
				});
			setLoading(false);
		}
	};

	const handleClickUpdate = async () => {
		const formToUpdate = {
			...customDomainForm,
			externalUrl: normalizeCustomDomainExternalUrl(customDomainForm.externalUrl),
			defaultUrl: normalizeCustomDomainUrls(customDomainForm.defaultUrl),
		};
		if (validateForm(formToUpdate) && formToUpdate.id) {
			setLoading(true);
			const {defaultUrl, externalUrl} = formToUpdate;
			await api.customDomain
				.update(formToUpdate.id, {defaultUrl, externalUrl})
				.then((res) => {
					// Update user context with new customDomain data
					const updatedUser = {
						...user,
						customDomain: {
							...(user.customDomain || {}),
							domain: externalUrl,
							path: defaultUrl,
							id: formToUpdate.id,
						}
					};
					setUser(updatedUser);

					setActiveSnackBar({
						state: true,
						name: ESnackBarType.success,
						message: "Successfully updated.\nPlease, wait 1 hour for your domain to start working.",
					});
				})
				.catch((res) => {
					setLoading(false);
					// TODO - Need to modify error handling
					let message = "Error occurred";
					setActiveSnackBar({
						state: true,
						name: ESnackBarType.error,
						message,
					});
				});
			setLoading(false);
		}
	};

	const handleChangeInput = (
		event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		// Do NOT trim or auto-prefix during typing — it prevents clearing and entering http://
		const value = event.target.value;
		let form = {...customDomainForm};
		switch (event.target.id) {
			case "defaultUrl":
				form.defaultUrl = value;
				break;
			case "externalUrl":
				form.externalUrl = value;
				break;
		}
		setCustomDomainForm(form);
		validateForm(form);
	};

	// Normalize CustomDomainUrl ( externalUrl ) — delete https:// prefix if exists (used before save/validate)
	const normalizeCustomDomainExternalUrl = (url: string): string => {
		let trimmed = url.trim();
		if (trimmed.length > 0 && trimmed.startsWith("http://")) {
			trimmed = trimmed.replace(/^http?:\/\//, "");
		}
		else if (trimmed.length > 0 && trimmed.startsWith("https://")) {
			trimmed = trimmed.replace(/^https?:\/\//, "");
		}
		if (trimmed.length > 0 && trimmed.endsWith("/")) {
			trimmed = trimmed.replace(/\/+$/, "");
		}

		return trimmed;
	};

	// Normalize CustomDomainUrls ( defaultUrl) — add https:// prefix if missing (used before save/validate)
	const normalizeCustomDomainUrls = (url: string): string => {
		const trimmed = url.trim();
		if (trimmed.length > 0 && !trimmed.startsWith("http://") && !trimmed.startsWith("https://")) {
			return "https://" + trimmed;
		}
		return trimmed;
	};

	useEffect(() => {
		if (
			/*customDomainForm.defaultUrl &&*/
			customDomainForm.externalUrl &&
			!checkFormHasErrors(errors)
		) {
			setStateDisabled(false);
			validateForm(customDomainForm);
		} else {
			setStateDisabled(true);
		}
	}, [customDomainForm]);

	useEffect(() => {
		if(user.customDomain &&
			(customDomainForm.id !== user.customDomain.id ||
			 customDomainForm.externalUrl !== user.customDomain.domain ||
			 customDomainForm.defaultUrl !== user.customDomain.path)) {
			setCustomDomainForm({
				externalUrl: user.customDomain.domain,
				defaultUrl: user.customDomain.path,
				id: user.customDomain.id,
			})
			setIsEdit(true);
		}
	}, [user.customDomain]);

	const checkFormHasErrors = (obj: any) => {
		let arr = [];
		for (let key of Object.keys(obj)) {
			arr.push(obj[key]);
		}
		return arr.findIndex((el) => el.length > 0) !== -1;
	};

	const validateForm = (form: TCustomDomain): any => {
		const errs = {...errors};
		let res = true;
		const regExpURLDefault = /^(https?:\/\/)?(www\.)?([\w\-]+\.)+[\w\-]{2,}(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/;
		// const regExpURLDefault = /^https:\/\/[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(?:\.[a-zA-Z]{2,})*\/[A-Za-z0-9]+$/;
		// const regExpURLExternal = /^https:\/\/[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(?:\.[a-zA-Z]{2,})*\/[A-Za-z0-9]+$/;


		// if (!form.defaultUrl || form.defaultUrl.length === 0) {
		// 	errs.defaultUrl = "Default URL is required!";
		// 	res = false;
		// } else {
			errs.defaultUrl = "";
		// }

		if (!form.externalUrl || form.externalUrl.length === 0) {
			errs.externalUrl = "External URL is required!";
			res = false;
		} else {
			errs.externalUrl = "";
		}

		if (form.defaultUrl && !regExpURLDefault.test(form.defaultUrl)) {
			errs.defaultUrl = "Default URL is invalid!";
			res = false;
		}

		/*if (!regExpURLExternal.test(form.externalUrl)) {
			errs.externalUrl = "External URL is invalid!";
			res = false;
		}*/

		setErrors(errs);
		return res;
	};

	const clearForm = () => {
		setCustomDomainForm({
			...customDomainForm,
			defaultUrl: "",
			externalUrl: "",
		});
	};

	const SaveUpdateBtn = () => {
		if (isEdit) {
			return (
				<button
					type={"submit"}
					className={"btn icon icon__center btn_desktop"}
					disabled={stateDisabled}
					onClick={handleClickUpdate}
				>
					<img
						src={saveIcon}
						alt="save"
						aria-hidden="true"
						role="presentation"
					/>
					Update
				</button>
			)
		} else {
			return (
				<button
					type={"submit"}
					className={"btn icon icon__center btn_desktop"}
					disabled={stateDisabled}
					onClick={handleClickSave}
				>
					<img
						src={saveIcon}
						alt="save"
						aria-hidden="true"
						role="presentation"
					/>
					Save
				</button>
			)
		}

	}

	return (
		<>
			<section className={`bg-gray_light account-edit`}>
				<div className={`${style.container}`}>
					<div className={`${style.acc_edit__wrapper}  edit-card`}>
						<h2
							className="text-center ff-gilroy"
							style={{marginBottom: 20, fontSize: "1.3rem"}}
						>
							Custom domain
						</h2>
						<div className={"mb-10 flex-1"}>
							<div className={`${style.white_label_item}`}>
								<span>1</span>
								<p>Make sure you have the access to your Domain management and DNS records management.</p>
								{/*<p>Where the first step is to open their domain management and follow to DNS settings.</p>*/}
							</div>
							<div className={`${style.white_label_item}`}>
								<span>2</span>
								<p>Create the <strong>A</strong> type record where the name value is equal to the domain or subdomain you are going to use and the IP address value is: <strong>{ourServerIP}</strong>.</p>
								{/*<p>Second is to add A record with the following parameters: 192.168.0.1</p>*/}
							</div>
							<div className={`${style.white_label_item}`}>
								<span>3</span>
								<p> Enter your domain or subdomain you used in the A type record in the External URL field below.</p>
								{/*<p>The third is to add the default URL in the field below. Then to enter the External domain.</p>*/}
							</div>
							<div className={`${style.white_label_item}`}>
								<span>4</span>
								<p>Enter the default URL for the redirect if necessary.</p>
								{/*<p>Then to pass the domain validation by entering the validation code in the HTML header of their web site.</p>*/}
							</div>
							<TextField
								id="externalUrl"
								name="externalUrl"
								label="External URL (Your domain)"
								value={customDomainForm.externalUrl}
								onChange={handleChangeInput}
								type="text"
								helperText={errors.externalUrl}
								error={!!errors.externalUrl}
								variant="outlined"
							/>
						<TextField
							id="defaultUrl"
							name="defaultUrl"
							label="Default URL (Your redirection URL)"
							value={customDomainForm.defaultUrl}
							helperText={errors.defaultUrl}
							error={!!errors.defaultUrl}
							onChange={handleChangeInput}
							type="text"
							variant="outlined"
						/>
						</div>
						<SaveUpdateBtn/>
					</div>
				</div>
			</section>
		</>
	);
};

export default CustomDomain;
